// 全局类型声明
import {
  type EnvironmentInfo,
  type PlatformCapabilities,
  PlatformEnum,
} from '../utils';

declare const wx: any;
declare const wxNative: any;

/**
 * 环境检测器
 * 负责检测当前运行的环境和平台能力
 */
export class EnvironmentDetector {
  /**
   * 检测当前平台类型
   */
  static detectPlatform(): PlatformEnum {
    // 检测Liteapp环境
    if (
      typeof lite !== 'undefined'
      && typeof lite.addEventListener === 'function'
    ) {
      return PlatformEnum.LiteApp;
    }

    // 检测小程序环境
    if (typeof wx !== 'undefined' && typeof wx.request === 'function') {
      return PlatformEnum.Miniprogram;
    }

    // 检测仿原生小程序环境
    if (
      typeof wxNative !== 'undefined'
      && typeof wxNative.webTransfer === 'function'
    ) {
      return PlatformEnum.Miniprogram;
    }

    // 降级到Web环境
    return PlatformEnum.Web;
  }

  /**
   * 检测是否为移动端
   */
  static detectIsMobile(): boolean {
    try {
      // 在浏览器环境中检测
      if (typeof navigator !== 'undefined' && navigator.userAgent) {
        return /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      }

      // 在Liteapp/Uniapp环境中，默认为移动端
      const platformType = this.detectPlatform();
      if (platformType === PlatformEnum.LiteApp) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * 检测是否支持触摸
   */
  static detectIsTouch(): boolean {
    try {
      // 在浏览器环境中检测
      if (typeof window !== 'undefined') {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      }

      // 在移动端环境中，默认为触摸
      const platformType = this.detectPlatform();
      if (platformType === PlatformEnum.LiteApp) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * 检测平台能力
   */
  static detectCapabilities(platformType: PlatformEnum): PlatformCapabilities {
    const capabilities: PlatformCapabilities = {
      localStorage: false,
      fetch: false,
      requestAnimationFrame: false,
      addEventListener: false,
      scrollEvents: false,
      resizeEvents: false,
    };

    try {
      switch (platformType) {
        case PlatformEnum.Web:
          capabilities.localStorage = typeof localStorage !== 'undefined';
          capabilities.fetch = typeof fetch !== 'undefined';
          capabilities.requestAnimationFrame =            typeof requestAnimationFrame !== 'undefined';
          capabilities.addEventListener =            typeof addEventListener !== 'undefined';
          capabilities.scrollEvents = true;
          capabilities.resizeEvents = true;
          break;

        case PlatformEnum.LiteApp:
          capabilities.localStorage = false;
          capabilities.fetch = typeof lite !== 'undefined' && typeof fetch !== 'undefined';
          capabilities.requestAnimationFrame = false; // 不支持，使用setTimeout模拟
          capabilities.addEventListener =            typeof lite !== 'undefined'
            && typeof lite.addEventListener !== 'undefined';
          capabilities.scrollEvents = false; // Liteapp不支持
          capabilities.resizeEvents = false; // liteapp不支持
          break;

        case PlatformEnum.Miniprogram:
          capabilities.localStorage = false;
          capabilities.fetch = false;
          capabilities.requestAnimationFrame = false;
          capabilities.addEventListener = false;
          capabilities.scrollEvents = false;
          capabilities.resizeEvents = false;
          break;
      }
    } catch (error) {
      console.warn('平台能力检测失败:', error);
    }

    return capabilities;
  }

  /**
   * 获取完整的环境信息
   */
  static getEnvironmentInfo(): EnvironmentInfo {
    const platformType = this.detectPlatform();
    const isMobile = this.detectIsMobile();
    const isTouch = this.detectIsTouch();

    return {
      platformType,
      isMobile,
      isTouch,
      inBrowser: typeof window !== 'undefined',
    };
  }

  /**
   * 获取平台版本信息
   */
  static getPlatformVersion(platformType: PlatformEnum): string | undefined {
    try {
      switch (platformType) {
        case PlatformEnum.Web:
          return typeof navigator !== 'undefined'
            ? navigator.userAgent
            : undefined;

        case PlatformEnum.LiteApp:
          return typeof lite !== 'undefined'
            ? lite.system.getUserAgent()
            : undefined;

        case PlatformEnum.Miniprogram:
          return typeof wx !== 'undefined'
            && typeof wx.getSystemInfoSync === 'function'
            ? wx.getSystemInfoSync().version
            : undefined;
      }
    } catch (error) {
      console.warn('平台版本检测失败:', error);
    }

    return undefined;
  }
}
